/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { useTheme } from '@mui/material'
import React, { useMemo } from 'react'

import useT from '@/i18n/useT'

import { Colors } from './Colors'

type CircleIntersectionProps = {
  area1: number
  area2: number
  intersectionPercent: number // % of larger circle's area (0–100)
  radius?: number
}

const CircleIntersection: React.FC<CircleIntersectionProps> = ({
  area1,
  area2,
  intersectionPercent,
  radius = 150,
}) => {
  const { t } = useT()

  const { px1, px2, offset } = useMemo(() => {
    const r1 = Math.sqrt(area1 / Math.PI)
    const r2 = Math.sqrt(area2 / Math.PI)

    const px1 = radius
    const scale = px1 / r1
    const px2 = r2 * scale

    const targetArea = (intersectionPercent / 100) * Math.PI * px1 ** 2

    const intersectionArea = (R1: number, R2: number, d: number): number => {
      if (d >= R1 + R2) return 0
      if (d <= Math.abs(R1 - R2)) return Math.PI * Math.min(R1, R2) ** 2

      const part1 =
        R1 ** 2 * Math.acos((d ** 2 + R1 ** 2 - R2 ** 2) / (2 * d * R1))
      const part2 =
        R2 ** 2 * Math.acos((d ** 2 + R2 ** 2 - R1 ** 2) / (2 * d * R2))
      const part3 =
        0.5 *
        Math.sqrt(
          (-d + R1 + R2) * (d + R1 - R2) * (d - R1 + R2) * (d + R1 + R2),
        )

      return part1 + part2 - part3
    }

    const findOffset = (): number => {
      let low = Math.abs(px1 - px2)
      let high = px1 + px2
      let mid = 0

      for (let i = 0; i < 100; i++) {
        mid = (low + high) / 2
        const area = intersectionArea(px1, px2, mid)

        if (Math.abs(area - targetArea) < 0.5) break
        if (area < targetArea) high = mid
        else low = mid
      }

      return mid
    }

    const offset = findOffset()

    return {
      px1,
      px2,
      offset,
    }
  }, [area1, area2, intersectionPercent])

  const cx1 = px1
  const cy = Math.max(px1, px2)
  const cx2 = cx1 + offset

  const theme = useTheme()

  return (
    <div
      style={{
        textAlign: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width={px1 + px2 + offset} height={Math.max(px1, px2) * 2}>
        {/* Base circles */}
        <circle cx={cx1} cy={cy} r={px1} fill={Colors.PLANGORAM} />
        <circle cx={cx2} cy={cy} r={px2} fill={Colors.REALOGRAM} />

        {/* Intersection mask */}
        <defs>
          <clipPath id="intersect-clip">
            <circle cx={cx1} cy={cy} r={px1} />
          </clipPath>
        </defs>
        <circle
          cx={cx2}
          cy={cy}
          r={px2}
          fill={theme.palette.background.paper}
          clipPath="url(#intersect-clip)"
          opacity={1}
        />
        <circle
          cx={cx2}
          cy={cy}
          r={px2}
          fill={Colors.CORRECTOGRAM}
          clipPath="url(#intersect-clip)"
          opacity={1}
        />
      </svg>

      {/* Key / Legend */}
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 20,
                backgroundColor: Colors.PLANGORAM,
                borderRadius: '50%',
              }}
            />
            <span>{t('compliance.vennDiagram.legend.planogram')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 20,
                backgroundColor: Colors.CORRECTOGRAM,
                borderRadius: '50%',
              }}
            />
            <span>{t('compliance.vennDiagram.legend.overlap')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 20,
                backgroundColor: Colors.REALOGRAM,
                borderRadius: '50%',
              }}
            />
            <span>{t('compliance.vennDiagram.legend.actual')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CircleIntersection
